import React from 'react';
import useCountdown from 'hooks/countdown';
import { readSessionItem, removeSessionItem, writeSessionItem } from 'utils/session-storage';

/**
 * `useCountdown`, переживающий перезагрузку страницы.
 *
 * В хранилище лежит не остаток секунд, а абсолютный дедлайн,
 * поэтому отсчёт остаётся верным, даже если вкладка была неактивна:
 * дедлайн из прошлого при восстановлении просто отбрасывается.
 */
export default function usePersistedCountdown(storageKey: string) {
  const {
    left, active, start, stop,
  } = useCountdown();

  React.useEffect(() => {
    const deadline = readSessionItem<number | null>(storageKey, null);
    if (!deadline) return;

    const rest = Math.ceil((deadline - Date.now()) / 1000);
    if (rest > 0) start(rest);
    else removeSessionItem(storageKey);
    // истёкший в памяти отсчёт ключ не чистит: чтение всё равно
    // отбрасывает прошедший дедлайн, а sessionStorage умрёт вместе с вкладкой
  }, [storageKey, start]);

  const persistedStart = React.useCallback((seconds: number) => {
    if (seconds > 0) writeSessionItem(storageKey, Date.now() + seconds * 1000);
    else removeSessionItem(storageKey);
    start(seconds);
  }, [storageKey, start]);

  const persistedStop = React.useCallback(() => {
    removeSessionItem(storageKey);
    stop();
  }, [storageKey, stop]);

  return {
    left, active, start: persistedStart, stop: persistedStop,
  };
}
